const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

let initialized = false;
let callbackRef = null;

export function setGoogleCredentialHandler(handler) {
  callbackRef = handler;
}

export function ensureGoogleIdentityInitialized() {
  if (!GOOGLE_CLIENT_ID || !window.google?.accounts?.id) return false;
  if (initialized) return true;

  initialized = true;

  window.google.accounts.id.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => {
      if (callbackRef) callbackRef(response);
    },
    auto_select: false,
    cancel_on_tap_outside: true,
  });

  return true;
}

export function waitForGoogleIdentity(maxWaitMs = 8000) {
  return new Promise((resolve) => {
    if (ensureGoogleIdentityInitialized()) {
      resolve(true);
      return;
    }

    const started = Date.now();
    const timer = window.setInterval(() => {
      if (ensureGoogleIdentityInitialized()) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() - started >= maxWaitMs) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 200);
  });
}

function measureButtonWidth(container) {
  if (!container) return null;
  const width = container.clientWidth || container.offsetWidth || 0;
  if (width < 120) return null;
  return Math.min(400, Math.max(280, Math.floor(width)));
}

/** GIS caps button width at 400px — scale to fill full container. */
export function applyGoogleButtonFullWidth(container) {
  if (!container) return;

  const renderedWidth = measureButtonWidth(container);
  if (!renderedWidth) return;

  const containerWidth = container.clientWidth || renderedWidth;
  const host = container.querySelector(':scope > div');
  if (!host) return;

  host.style.width = `${renderedWidth}px`;
  host.style.maxWidth = 'none';

  if (containerWidth > renderedWidth + 2) {
    const scale = containerWidth / renderedWidth;
    host.style.transform = `scaleX(${scale})`;
    host.style.transformOrigin = 'left center';
  } else {
    host.style.transform = '';
    host.style.transformOrigin = '';
  }

  const iframe = host.querySelector('iframe');
  if (iframe) {
    iframe.style.height = '48px';
    iframe.style.minHeight = '48px';
  }
}

export function renderGoogleButton(container, options = {}) {
  if (!container || !window.google?.accounts?.id) return false;
  if (!ensureGoogleIdentityInitialized()) return false;

  const width = options.width || measureButtonWidth(container) || 320;
  container.innerHTML = '';
  window.google.accounts.id.renderButton(container, {
    theme: 'outline',
    size: 'large',
    width,
    text: 'signin_with',
    locale: 'vi',
    shape: 'rectangular',
    ...options,
  });

  requestAnimationFrame(() => applyGoogleButtonFullWidth(container));
  return true;
}

export function mountGoogleSignInButton(container) {
  if (!container || !GOOGLE_CLIENT_ID) return () => {};

  let cancelled = false;
  let lastWidth = 0;
  let observer = null;

  const tryRender = () => {
    if (cancelled || !container.isConnected) return false;

    const width = measureButtonWidth(container);
    if (!width) return false;

    if (width === lastWidth && container.querySelector('iframe')) {
      applyGoogleButtonFullWidth(container);
      return true;
    }

    lastWidth = width;
    renderGoogleButton(container, { width });
    return true;
  };

  const scheduleRender = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!tryRender() && !cancelled) {
          window.setTimeout(() => tryRender(), 120);
        }
      });
    });
  };

  waitForGoogleIdentity().then((ready) => {
    if (cancelled || !ready) return;

    scheduleRender();

    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (cancelled) return;
        if (container.querySelector('iframe')) {
          applyGoogleButtonFullWidth(container);
          return;
        }
        const width = measureButtonWidth(container);
        if (width && Math.abs(width - lastWidth) > 24) {
          scheduleRender();
        }
      });
      observer.observe(container);
    }
  });

  return () => {
    cancelled = true;
    observer?.disconnect();
  };
}

export function preloadGoogleIdentity() {
  if (!GOOGLE_CLIENT_ID) return;
  waitForGoogleIdentity();
}

export { GOOGLE_CLIENT_ID };
